import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  take,
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HttpService } from '../http-service/http.service';
import { API_ENDPOINTS } from 'src/app/config/api-endpoints';
import { ClientWithEntitiesDto, EntityDto } from 'src/app/model';

@Injectable({
  providedIn: 'root',
})
export class ClientEntityUserRoleService {
  private readonly apiUrl = `${environment.apiBaseURL}${API_ENDPOINTS.clientUserRoles.GetClientUserRole}`;

  public clientUserRole$?: Observable<ClientWithEntitiesDto[]>;

  constructor(private http: HttpService) {}

  // API methods
  private getClientUserRoles(): Observable<ClientWithEntitiesDto[]> {
    return this.http.get<ClientWithEntitiesDto[]>(this.apiUrl);
  }

  public load(): Observable<ClientWithEntitiesDto[]> {
    if (!this.clientUserRole$) {
      this.clientUserRole$ = this.getClientUserRoles().pipe(
        take(1), // only first emission
        map(this.normalizeData), // normalize IDs, trim, lowercase
        shareReplay(1), // cache & replay,
        distinctUntilChanged(),
        catchError(() => of([])) // on error return empty array
      );
    }
    return this.clientUserRole$;
  }

  public hasAccess(clientId: string, entityId: string): Observable<boolean> {
    return this.clientUserRole$!.pipe(
      filter((clients) => clients !== null),
      map((clients: ClientWithEntitiesDto[]) =>
        clients.some(
          (client: ClientWithEntitiesDto) =>
            client.ClientId === clientId &&
            client.Entities?.some((e: EntityDto) => e.EntityId === entityId)
        )
      ),
      distinctUntilChanged()
    );
  }

  public getEntityTaxPeriodDetails(
    clientId: string,
    entityId: string,
    clientTaxPeriodId: string
  ): Observable<{ entityName: string; month: number; year: number } | null> {
    const periodId = +clientTaxPeriodId;

    return this.clientUserRole$!.pipe(
      filter((clients) => clients !== null),
      map((clients) => {
        // find the matching client
        const client = clients.find((c) => c.ClientId === clientId);
        if (!client) return null;

        // find the matching entity
        const entity = client.Entities?.find((e) => e.EntityId === entityId);
        if (!entity) return null;

        // find the requested tax period
        const taxPeriod = client.ClientTaxPeriods?.find(
          (tp) => tp.ClientTaxPeriodId === periodId
        );
        if (!taxPeriod) return null;

        // return the desired payload
        return {
          entityName: entity.EntityName,
          month: taxPeriod.Month,
          year: taxPeriod.Year,
        };
      })
    );
  }

  private normalizeData(
    data: ClientWithEntitiesDto[]
  ): ClientWithEntitiesDto[] {
    return data.map((c) => ({
      ...c,
      ClientId: c.ClientId.trim().toLowerCase(),
      Entities:
        c.Entities?.map((e) => ({
          ...e,
          EntityId: e.EntityId.trim().toLowerCase(),
        })) ?? [],
    }));
  }
}
